import React from 'react';
import './ProjectionsTable.css';
import ProjectionsChart from './ProjectionsChart';
import APP_CONFIG, { TIER_ORDER, getCurrencySymbol } from '../config/appConfig';
import { buildProjections } from '../lib/pricing';

/**
 * Four-quarter revenue and cost projection.
 *
 * Every figure here is a period total (a quarter is three months, annual is
 * twelve), so amounts are formatted with isMonthly=false — they must not be
 * re-multiplied by the yearly billing toggle.
 */
const ProjectionsTable = ({
    revenue,
    tierCosts,
    monthlyGrowthRate,
    setMonthlyGrowthRate,
    currency,
    formatCurrency,
    revenueModel,
    revenueDrivers,
    pricingMode
}) => {
    const packages = APP_CONFIG.packages;
    // Advanced has no published price, so it cannot be projected.
    const pricedTiers = TIER_ORDER.filter(t => !packages[t].custom);
    const isLegacy = pricingMode === 'legacy';

    const getPrimaryDriver = () => {
        if (!revenueDrivers) return { name: 'Revenue', value: null };
        switch (revenueModel) {
            case 'SVOD': return { name: 'Subscribers', value: revenueDrivers.subscribers };
            case 'AVOD': return { name: 'Impressions', value: revenueDrivers.impressions };
            case 'TVOD': return { name: 'Transactions', value: revenueDrivers.transactions };
            case 'HYBRID': return { name: 'Mixed Revenue', value: null };
            default: return { name: 'Revenue', value: null };
        }
    };

    const projections = buildProjections({ revenue, tierCosts, monthlyGrowthRate });
    const { quarterly, annual } = projections;
    const primaryDriver = getPrimaryDriver();
    const money = (amount) => formatCurrency(amount, false);

    return (
        <div className="projections-table">
            <div className="projections-header">
                <h2>4-Quarter Projections</h2>
                <div className="growth-input">
                    <label>
                        Monthly Growth Rate (%)
                        <div className="tooltip-container">
                            <span className="question-mark">?</span>
                            <div className="tooltip">
                                Expected monthly growth rate for your revenue. This compounds over time to show quarterly projections.
                            </div>
                        </div>
                    </label>
                    <input
                        type="number"
                        value={monthlyGrowthRate}
                        onChange={(e) => setMonthlyGrowthRate(Number(e.target.value))}
                        min="0"
                        max="100"
                        step="0.1"
                    />
                </div>
            </div>

            <ProjectionsChart
                projections={projections}
                revenue={revenue}
                tierCosts={tierCosts}
                currencySymbol={getCurrencySymbol(currency)}
                monthlyGrowthRate={monthlyGrowthRate}
            />

            <div className="table-container">
                <table className="projections-table-grid">
                    <thead>
                        <tr>
                            <th>Metric</th>
                            <th>Monthly</th>
                            {quarterly.map((projection, index) => (
                                <th key={index}>{projection.quarterShort}</th>
                            ))}
                            <th>Annual</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr className="table-note">
                            <td colSpan={quarterly.length + 3}>
                                All values in {currency || 'USD'}. Quarter columns are three-month totals.
                            </td>
                        </tr>

                        {primaryDriver.value && (
                            <tr>
                                <td className="metric-name">{primaryDriver.name}</td>
                                <td className="metric-value">
                                    {primaryDriver.name === 'Impressions'
                                        ? (primaryDriver.value / 1000).toLocaleString() + 'K'
                                        : primaryDriver.value.toLocaleString()}
                                </td>
                                {quarterly.map((projection, index) => {
                                    const driverValue = primaryDriver.value * Math.pow(1 + (monthlyGrowthRate / 100), index * 3) * 3;
                                    return (
                                        <td key={index} className="metric-value">
                                            {primaryDriver.name === 'Impressions'
                                                ? Math.round(driverValue / 1000).toLocaleString() + 'K'
                                                : Math.round(driverValue).toLocaleString()}
                                        </td>
                                    );
                                })}
                                <td className="metric-value">-</td>
                            </tr>
                        )}

                        <tr>
                            <td className="metric-name">Revenue</td>
                            <td className="metric-value">{money(revenue)}</td>
                            {quarterly.map((projection, index) => (
                                <td key={index} className="metric-value">{money(projection.revenue)}</td>
                            ))}
                            <td className="metric-value">{money(annual.revenue)}</td>
                        </tr>

                        {pricedTiers.map(tierKey => (
                            <React.Fragment key={tierKey}>
                                <tr>
                                    <td className="metric-name package-name">{packages[tierKey].name} — Cost</td>
                                    <td className="metric-value">
                                        {money(tierCosts[tierKey].monthlyRecurringCost)}
                                    </td>
                                    {quarterly.map((projection, index) => (
                                        <td key={index} className="metric-value">
                                            {money(projection.tiers[tierKey].totalCost)}
                                            {projection.isFirstQuarter && projection.tiers[tierKey].oneTimeCost > 0 && (
                                                <div className="one-time-note">*Includes one-time app costs</div>
                                            )}
                                        </td>
                                    ))}
                                    <td className="metric-value">{money(annual.tiers[tierKey].totalCost)}</td>
                                </tr>
                                <tr>
                                    <td className="metric-name package-name">{packages[tierKey].name} — Profit</td>
                                    <td className={`metric-value profit ${revenue - tierCosts[tierKey].monthlyRecurringCost < 0 ? 'loss' : ''}`}>
                                        {money(revenue - tierCosts[tierKey].monthlyRecurringCost)}
                                    </td>
                                    {quarterly.map((projection, index) => (
                                        <td
                                            key={index}
                                            className={`metric-value profit ${projection.tiers[tierKey].profit < 0 ? 'loss' : ''}`}
                                        >
                                            {money(projection.tiers[tierKey].profit)}
                                        </td>
                                    ))}
                                    <td className={`metric-value profit ${annual.tiers[tierKey].profit < 0 ? 'loss' : ''}`}>
                                        {money(annual.tiers[tierKey].profit)}
                                    </td>
                                </tr>
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
                {isLegacy && (
                    <p className="projections-note">
                        Legacy pricing: one-time app development fees are charged in the first quarter only.
                    </p>
                )}
            </div>
        </div>
    );
};

export default ProjectionsTable;
